import { ref, type DirectiveBinding } from 'vue'
import { defineStore } from 'pinia'

export const useTooltipStore = defineStore('tooltip', () => {
  const visible = ref(false)
  const message = ref('')
  const position = ref({ x: 0, y: 0 })
  const direction = ref('top')
  const width = ref('fit')
  let timeout: ReturnType<typeof setTimeout> | null = null
  let scrollListenerAttached: boolean = false

  function show(el: HTMLElement, binding: DirectiveBinding) {
    timeout && clearTimeout(timeout)

    if (!binding.value) return

    const rect = el.getBoundingClientRect()
    const args = binding.arg ? binding.arg.split(':') : []
    const dir = args.find((d: string) => ['top', 'right', 'bottom', 'left'].includes(d)) ?? 'top'
    const wd = args.find((w: string) => ['fit', 'min'].includes(w)) ?? 'fit'
    const x = dir === 'left' ? rect.x : dir === 'right' ? rect.x + rect.width : rect.x + rect.width / 2
    const y = dir === 'top' ? rect.y : dir === 'bottom' ? rect.y + rect.height : rect.y + rect.height / 2

    message.value = binding.value
    direction.value = dir
    position.value = { x: x + window.scrollX, y: y + window.scrollY }
    visible.value = true
    width.value = wd

    attachScrollListener()
  }

  function hide() {
    timeout = setTimeout(() => {
      visible.value = false
      detachScrollListener()
    }, 500)
  }

  function preventHide() {
    timeout && clearTimeout(timeout)
  }

  function hideImmediately() {
    timeout && clearTimeout(timeout)
    visible.value = false
  }

  function attachScrollListener() {
    if (scrollListenerAttached) return
    scrollListenerAttached = true

    window.addEventListener('scroll', hideImmediately, { passive: true, capture: true })
  }

  function detachScrollListener() {
    if (!scrollListenerAttached) return
    scrollListenerAttached = false

    window.removeEventListener('scroll', hideImmediately)
  }

  return { message, visible, direction, width, position, show, hide, preventHide, hideImmediately }
})
